import { menuItems, getAvailableDate, formatPortionLabel } from './foods';
import api from '@/Services/api';

export interface MenuGroupFood {
    id: number;
    portion: number;
    price: number;
}

export interface MenuGroup {
    id: number;
    name: string;
    pick: number;
    foods: MenuGroupFood[];
}

export interface MenuOffer {
    id: number;
    name: string;
    slug: string;
    description: string;
    image_path: string;
    discount_percentage: number;
    price: number;
    groups: MenuGroup[];
}

export interface GroupedFoodItem {
    id: number;
    name: string;
    slug: string;
    portion_label?: string;
    image_path?: string;
}

export interface GroupedFoodGroup {
    groupName: string;
    foods: GroupedFoodItem[];
}

type SelectedItems = Map<number, number[]>;
type MenuFoodItem = (typeof menuItems)[number];

export interface MenuCardData {
    id: number;
    name: string;
    slug: string;
    description: string;
    image_path: string;
    discount_percentage: number;
    price: number;
    group_summary: string;
    original_price: number;
    discounted_price: number;
    total_weight: number;
    category_ids: number[];
    isFixed: boolean;
    foodList: string[];
    groups: MenuGroup[];
    groupedFoods: GroupedFoodGroup[];
}

export const getMenuGroups = (menu: Pick<MenuOffer, 'groups'>): MenuGroup[] => {
    return Array.isArray(menu.groups) ? menu.groups : [];
};

const normalizeMenuGroupFood = (food: unknown): MenuGroupFood | null => {
    if (!food || typeof food !== 'object') return null;

    const candidate = food as Partial<MenuGroupFood>;
    const id = Number(candidate.id);

    if (!Number.isFinite(id)) return null;

    return {
        id,
        portion: Number(candidate.portion ?? 0),
        price: Number(candidate.price ?? 0),
    };
};

const normalizeMenuGroup = (group: unknown): MenuGroup | null => {
    if (!group || typeof group !== 'object') return null;

    const candidate = group as Partial<MenuGroup>;
    const id = Number(candidate.id);

    if (!Number.isFinite(id)) return null;

    return {
        id,
        name: typeof candidate.name === 'string' ? candidate.name : '',
        pick: Number(candidate.pick ?? 0),
        foods: Array.isArray(candidate.foods)
            ? candidate.foods.map(normalizeMenuGroupFood).filter((food): food is MenuGroupFood => food !== null)
            : [],
    };
};

const normalizeMenu = (menu: unknown): MenuOffer | null => {
    if (!menu || typeof menu !== 'object') return null;

    const candidate = menu as Partial<MenuOffer>;
    const id = Number(candidate.id);

    if (!Number.isFinite(id)) return null;

    return {
        id,
        name: typeof candidate.name === 'string' ? candidate.name : '',
        slug: typeof candidate.slug === 'string' ? candidate.slug : '',
        description: typeof candidate.description === 'string' ? candidate.description : '',
        image_path: typeof candidate.image_path === 'string' ? candidate.image_path : '',
        discount_percentage: Number(candidate.discount_percentage ?? 0),
        price: Number(candidate.price ?? 0),
        groups: Array.isArray(candidate.groups)
            ? candidate.groups.map(normalizeMenuGroup).filter((group): group is MenuGroup => group !== null)
            : [],
    };
};

const normalizeImagePath = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http') || path.startsWith('/') || path.startsWith('data:')) {
        return path;
    }
    return `/${path}`;
};

export const fetchAllMenus = async (): Promise<MenuOffer[]> => {
    const { data } = await api.get('/api/menus');
    return Array.isArray(data) ? data.map(normalizeMenu).filter((menu): menu is MenuOffer => menu !== null) : [];
};

export const fetchMenuBySlug = async (slug: string): Promise<MenuOffer | undefined> => {
    try {
        const { data } = await api.get(`/api/menus/${encodeURIComponent(slug)}`);
        return normalizeMenu(data) ?? undefined;
    } catch {
        return undefined;
    }
};

export const fetchMenuById = async (id: number): Promise<MenuOffer | undefined> => {
    const menus = await fetchAllMenus();
    return menus.find((m) => m.id === id);
};

const getFoodById = (id: number): MenuFoodItem | undefined => menuItems.find((f) => f.id === id);

const getGroupFood = (group: MenuGroup, foodId: number): MenuGroupFood | undefined => {
    return group.foods.find((food) => food.id === foodId);
};

const getResolvedFoodPrice = (group: MenuGroup, foodId: number): number => {
    return Number(getGroupFood(group, foodId)?.price ?? getFoodById(foodId)?.price ?? 0);
};

const getResolvedFoodPortionLabel = (group: MenuGroup, foodId: number): string | undefined => {
    const food = getFoodById(foodId);
    const portion = getGroupFood(group, foodId)?.portion ?? food?.portion_quantity;
    return formatPortionLabel(portion, 'g');
};

export const getMenuOriginalPrice = (menu: MenuOffer, selectedItems: SelectedItems): number => {
    if (Number(menu.price) > 0) return Number(menu.price);
    let total = 0;
    getMenuGroups(menu).forEach((group) => {
        const selectedIds = selectedItems.get(group.id) ?? [];
        selectedIds.forEach((foodId) => {
            total += getResolvedFoodPrice(group, foodId);
        });
    });
    return total;
};

export const getMenuDiscountedPrice = (menu: MenuOffer, selectedItems: SelectedItems): number => {
    const original = getMenuOriginalPrice(menu, selectedItems);
    return Math.round(original * (1 - menu.discount_percentage / 100) * 100) / 100;
};

export const getMenuSavings = (menu: MenuOffer, selectedItems: SelectedItems): number => {
    const original = getMenuOriginalPrice(menu, selectedItems);
    const discounted = getMenuDiscountedPrice(menu, selectedItems);
    return Math.round((original - discounted) * 100) / 100;
};

export const getMenuTotalWeight = (
    menu: MenuOffer,
    selectedItems: SelectedItems,
    foodUpdates?: Map<number, { weight_percentage?: number }>
): number => {
    const uniqueIds = new Set<number>();
    getMenuGroups(menu).forEach((group) => {
        const selectedIds = selectedItems.get(group.id) ?? [];
        selectedIds.forEach((id) => uniqueIds.add(id));
    });
    let totalWeight = 0;
    uniqueIds.forEach((id) => {
        const update = foodUpdates?.get(id);
        if (update && update.weight_percentage !== undefined) {
            totalWeight += update.weight_percentage;
        } else {
            const food = getFoodById(id);
            if (food) totalWeight += food.weight_percentage ?? 0;
        }
    });
    return totalWeight;
};

export const getMenuMaxPrepDays = (menu: MenuOffer, selectedItems: SelectedItems): number => {
    let maxDays = 0;
    getMenuGroups(menu).forEach((group) => {
        const selectedIds = selectedItems.get(group.id) ?? [];
        selectedIds.forEach((foodId) => {
            const food = getFoodById(foodId);
            if (food?.preparation_days && food.preparation_days > maxDays) {
                maxDays = food.preparation_days;
            }
        });
    });
    return maxDays;
};

export const getMenuEarliestDate = (menu: MenuOffer, selectedItems: SelectedItems): Date | undefined => {
    const maxPrepDays = getMenuMaxPrepDays(menu, selectedItems);
    return getAvailableDate(maxPrepDays);
};

export const getGroupSummary = (menu: MenuOffer): string => {
    return getMenuGroups(menu)
        .map((g) => {
            const pickLabel = g.pick > 1 ? `${g.pick} ${g.name.toLowerCase()}s` : `1 ${g.name.toLowerCase()}`;
            return pickLabel;
        })
        .join(' · ');
};

export const isMenuSelectionComplete = (menu: MenuOffer, selectedItems: SelectedItems): boolean => {
    return getMenuGroups(menu).every((group) => {
        const selected = selectedItems.get(group.id) ?? [];
        return selected.length === group.pick;
    });
};

export const buildDefaultSelectedItems = (menu: MenuOffer): SelectedItems => {
    const selectedItems: SelectedItems = new Map();

    getMenuGroups(menu).forEach((group) => {
        selectedItems.set(group.id, group.foods.map((food) => food.id).slice(0, group.pick));
    });

    return selectedItems;
};

export const toMenuCard = (menu: MenuOffer): MenuCardData => {
    const normalizedImage = normalizeImagePath(menu.image_path);
    const groups = getMenuGroups(menu);
    const defaultSelectedItems = buildDefaultSelectedItems(menu);
    const fixedPrice = Number(menu.price ?? 0);
    const hasFixedPrice = fixedPrice > 0;

    return {
        id: menu.id,
        name: menu.name,
        slug: menu.slug,
        description: menu.description,
        image_path: normalizedImage,
        discount_percentage: menu.discount_percentage,
        price: fixedPrice,
        group_summary: getGroupSummary(menu),
        original_price: getMenuOriginalPrice(menu, defaultSelectedItems),
        discounted_price: getMenuDiscountedPrice(menu, defaultSelectedItems),
        total_weight: getMenuTotalWeight(menu, defaultSelectedItems),
        category_ids: getMenuCategoryIds(menu),
        isFixed: isMenuFixed(menu),
        foodList: getMenuFoodList(menu),
        groups,
        groupedFoods: getGroupedMenuFoods(menu),
    };
};

export const fetchMenuCards = async (): Promise<MenuCardData[]> => {
    const menus = await fetchAllMenus();
    return menus.map(toMenuCard);
};

export const getAllMenuOffers = (menus: MenuOffer[]) => menus;

export const getAvailableMenuOffers = (menus: MenuOffer[], availableIds: number[]) =>
    menus.filter((m) => availableIds.includes(m.id));

export const isMenuFixed = (menu: MenuOffer): boolean => {
    return getMenuGroups(menu).every((group) => group.pick === group.foods.length);
};

export const getMenuFoodList = (menu: MenuOffer): string[] => {
    const result: string[] = [];
    getMenuGroups(menu).forEach((group) => {
        group.foods.forEach(({ id }) => {
            const food = menuItems.find((f) => f.id === id);
            if (food) result.push(food.name);
        });
    });
    return result;
};

export const getGroupedMenuFoods = (menu: MenuOffer): GroupedFoodGroup[] => {
    return getMenuGroups(menu).map((group) => ({
        groupName: group.name,
        foods: group.foods
            .map(({ id }) => {
                const food = menuItems.find((f) => f.id === id);
                if (!food) return null;
                const groupedFood: GroupedFoodItem = {
                    id: food.id,
                    name: food.name,
                    slug: food.slug,
                    portion_label: getResolvedFoodPortionLabel(group, id) ?? food.portion_label,
                    image_path: food.image_path,
                };
                return groupedFood;
            })
            .filter((f): f is GroupedFoodItem => f !== null),
    }));
};

export const getMenuCategoryIds = (menu: MenuOffer): number[] => {
    const categoryIds = new Set<number>();

    getMenuGroups(menu).forEach((group) => {
        group.foods.forEach(({ id: foodId }) => {
            const food = getFoodById(foodId);
            if (food?.category_id) {
                categoryIds.add(Number(food.category_id));
            }
        });
    });

    return Array.from(categoryIds);
};
